import React, { useMemo } from "react";
import { IEvidence, ProgramData } from "@interfaces/Interfaces";
import { useRecoilValueLoadable } from "recoil";
import { evidenceAtom } from "@state/State";
import * as SPAPRPUtils from "@utils/programs/SPAPRPUtils";

type Props = {
  program?: ProgramData;
  collapse?: boolean;
};

const WPBACardDisplay: React.FC<Props> = (props) => {
  const allEvidence = useRecoilValueLoadable<IEvidence[] | null>(evidenceAtom);

  let programEvidence: IEvidence[] = [];

  if (allEvidence.state === "hasValue" && allEvidence.contents) {
    programEvidence = allEvidence.contents.filter((evidence: IEvidence) => {
      const evidenceJSON = evidence.evidenceJSON;
      const id = evidenceJSON?.programInfo?.programID || "";

      const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

      return id === props.program?.ID && onHoldReset !== 1 && evidence.draft !== true;
    });
  }

  const wpbaForms = props.program?.["WPBA Forms"] ?? [];

  const wpbaFormEvidence = useMemo(() => {
    const formEvidenceDefinitions = wpbaForms.flatMap((form) => form["Evidence Definitions"]?.[0]?.ID ?? []);

    return programEvidence.filter((evidence) => {
      return formEvidenceDefinitions.some(
        (item) => item === evidence.evidenceJSON.programInfo.programEvidenceDefinitionID
      );
    });
  }, [programEvidence, wpbaForms]);

  const wpbaSuperviseeEvidence = useMemo(() => {
    return wpbaFormEvidence.filter((evidence) => {
      return !evidence.evidenceJSON.programInfo.supervisorCopy && !evidence.evidenceJSON.programInfo.pending;
    });
  }, [wpbaFormEvidence]);

  const wpbaSuperviseePendingEvidence = useMemo(() => {
    return wpbaFormEvidence.filter((evidence) => {
      return !evidence.evidenceJSON.programInfo.supervisorCopy && SPAPRPUtils.isWPBAFormEvidencePending(evidence);
    });
  }, [wpbaFormEvidence]);

  const wpbaFormSupervisorEvidence = useMemo(() => {
    return wpbaFormEvidence.filter((evidence) => {
      return evidence.evidenceJSON.programInfo.supervisorCopy;
    });
  }, [wpbaFormEvidence]);

  const wpbaSuperviseeFormCount: number = new Set(wpbaSuperviseeEvidence).size;
  const wpbaSuperviseePendingCount: number = new Set(wpbaSuperviseePendingEvidence).size;
  const wpbaFormSupervisorCount: number = new Set(wpbaFormSupervisorEvidence).size;

  return (
    <div>
      <div className="mb-2 flex w-full flex-col">
        <div className="mb-1 flex w-full flex-row justify-between py-2 text-acc-15px font-bold text-grey-90">
          {"As clinician"}
        </div>
        <div className="h-11 w-full">
          <div className="flex flex-row justify-between py-2">
            <div className="flex max-w-half flex-col">{"Completed"}</div>
            <div className="flex size-5 items-center justify-center rounded-full bg-green-background">
              <div className="text-12px font-semibold text-green-text">{wpbaSuperviseeFormCount}</div>
            </div>
          </div>
          <div className="h-[1px] w-full bg-grey-30" />
        </div>
        <div className="h-11 w-full">
          <div className="flex flex-row justify-between py-2">
            <div className="flex max-w-half flex-col">{"Pending"}</div>
            <div className="flex size-5 items-center justify-center rounded-full bg-orange-20">
              <div className="text-12px font-semibold text-orange-text">{wpbaSuperviseePendingCount}</div>
            </div>
          </div>
          <div className="h-[1px] w-full bg-grey-30" />
        </div>
        <div className="mb-1 flex w-full flex-row justify-between py-2 text-acc-15px font-bold text-grey-90">
          {"As supervisor"}
        </div>
        <div className="h-11 w-full">
          <div className="flex flex-row justify-between py-2">
            <div className="flex max-w-half flex-col">{"Completed"}</div>
            <div className="flex size-5 items-center justify-center rounded-full bg-green-background">
              <div className="text-12px font-semibold text-green-text">{wpbaFormSupervisorCount}</div>
            </div>
          </div>
          <div className="h-[1px] w-full bg-grey-30" />
        </div>
      </div>
    </div>
  );
};

export default WPBACardDisplay;
